import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchSkillMetrics, fetchSkillAgents, fetchSkillEvidence, updateSkill, deleteSkill } from '../api/skills'
import MetricCard from './MetricCard'
import SkillPill from './SkillPill'
import CaseBadge from './CaseBadge'
import ConfirmDialog from './ConfirmDialog'
import EditSkillModal from './EditSkillModal'
import AssignSkillModal from './AssignSkillModal'
import Icon from './Icon'
import styles from './SkillDetailsModal.module.css'

// Max evidence cases the backend will return in one call (its `limit` is capped
// at 500). "Show more" requests min(total, this) and the chip hides once we've
// loaded this many, so it never dead-clicks on a skill with >500 cases.
const EVIDENCE_CAP = 500

// last_used is a calendar date — build it as a local date so a bare YYYY-MM-DD
// isn't shifted a day west of UTC (mirrors AgentSkillsModal.formatDate).
function formatDate(iso) {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// Level is ordinal text, so it sorts by rank, not alphabetically.
const LEVEL_RANK = { None: 0, Beginner: 1, Intermediate: 2, Advanced: 3, Expert: 4 }

// Per-column comparators (ascending); the active direction negates for desc.
// Every column is sortable.
const SORT_FNS = {
  name:      (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  level:     (a, b) => (LEVEL_RANK[a.level] ?? -1) - (LEVEL_RANK[b.level] ?? -1),
  count:     (a, b) => (a.count ?? 0) - (b.count ?? 0),
  last_used: (a, b) => String(a.last_used ?? '').localeCompare(String(b.last_used ?? '')),
}

/**
 * SkillDetailsModal — skill-centric detail overlay opened from a Skills Coverage card.
 * The inverse of AgentSkillsModal: the skill is fixed and each table row is a team
 * member who holds it. The team-member table is view-only here — no per-row
 * actions column (that's AgentSkillsModal's Teams-tab modal; this one only
 * assigns via the toolbar button and edits/deletes the whole skill via the footer).
 *
 * Self-fetching: loads the mini-metrics, the team-member list, and the evidence
 * cases on mount. The three bottom buttons are intentionally rendered but unwired.
 *
 * Props:
 *   skillId   {number|string} — the active skill's id
 *   skillName {string}        — display name (header + CaseBadge primary skill)
 *   skillType {string}        — Core | Technical | General (header pill)
 *   skillDescription {string} — evidence-based skill description (Edit dialog field)
 *   types     {string[]}      — valid skill types for the Edit dialog's type picker
 *   onClose   {function}      — called when the modal is dismissed
 *   onSkillMutated {function} — called once on close with skillId, but only if the
 *                               Assign Skill flow was used while the modal was open,
 *                               so the parent can refresh just that skill's card.
 *                               Optional.
 *   skillTarget {number}  — the skill's optimal coverage (target_agent_count), seeded into the
 *                           Edit dialog.
 *   onSkillUpdated {function} — called with (skillId, { name, type, description,
 *                               target_agent_count }) right
 *                               after the skill is renamed/reclassified/re-described via
 *                               the Edit dialog, so the parent can patch its coverage list
 *                               in place. Optional.
 *   onSkillDeleted {function} — called with skillId right after the skill is deleted, so
 *                               the parent can drop its coverage card. Optional.
 */
export default function SkillDetailsModal({
  skillId, skillName, skillType, skillDescription, skillTarget, types = [], onClose, onSkillMutated,
  onSkillUpdated, onSkillDeleted,
}) {
  // Live header identity — seeded from props but updated in place when the Edit dialog
  // saves, so the header reflects the rename/reclassify without a remount.
  const [name, setName] = useState(skillName)
  const [type, setType] = useState(skillType)
  const [description, setDescription] = useState(skillDescription)
  const [target, setTarget] = useState(skillTarget)

  // Edit dialog state: open flag, in-flight guard, and last error (keeps the dialog open).
  const [editOpen, setEditOpen] = useState(false)
  const [editBusy, setEditBusy] = useState(false)
  const [editError, setEditError] = useState('')
  const [metrics, setMetrics] = useState([])
  const [agents, setAgents] = useState([])
  const [evidence, setEvidence] = useState([])
  const [evidenceTotal, setEvidenceTotal] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loading, setLoading] = useState({ metrics: true, agents: true, evidence: true })
  const [error, setError] = useState(null)

  // Team-member sort: { key, dir } or null (backend order). A header click cycles
  // none → asc → desc → none; a different column starts fresh at asc.
  const [sort, setSort] = useState(null)

  // Pending ConfirmDialog props (+ the action to run on confirm), or null.
  const [confirm, setConfirm] = useState(null)

  // Assign-skill modal open flag.
  const [assignOpen, setAssignOpen] = useState(false)

  // True while the whole-skill delete is in flight — disables the footer Delete button.
  const [deleting, setDeleting] = useState(false)

  // Set once any row action is confirmed; read on close to decide whether the
  // parent needs to refresh this skill's card. A ref (not state) — it only gates a
  // close-time callback and must never trigger a re-render. Deliberately flipped on
  // EVERY successful action, so the modal never has to reason about which actions
  // affect the coverage card.
  const dirty = useRef(false)

  // The modal mounts fresh per skill, so initial state is the reset. `active`
  // guards each slice against a late response after unmount.
  useEffect(() => {
    let active = true
    const done = key => { if (active) setLoading(prev => ({ ...prev, [key]: false })) }

    fetchSkillMetrics(skillId)
      .then(d => { if (active) setMetrics(Array.isArray(d) ? d : []) })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => done('metrics'))

    fetchSkillAgents(skillId)
      .then(d => { if (active) setAgents(d.agents ?? []) })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => done('agents'))

    fetchSkillEvidence(skillId)
      .then(d => {
        if (!active) return
        setEvidence(d.evidence ?? [])
        setEvidenceTotal(d.total ?? 0)
      })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => done('evidence'))

    return () => { active = false }
  }, [skillId])

  // Single close path for every dismissal (X button + backdrop). Fires the
  // refresh callback exactly once, and only if something actually changed.
  function handleClose() {
    if (dirty.current) onSkillMutated?.(skillId)
    onClose()
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) handleClose()
  }

  // Persist a rename/reclassify from the Edit dialog. On success update the header in
  // place and tell the parent so its coverage card reflects the new name/type; on
  // failure keep the dialog open with the backend's message.
  async function handleEditSave({ skillName: newName, skillType: newType, description: newDescription, targetAgentCount }) {
    setEditBusy(true)
    setEditError('')
    try {
      const res = await updateSkill(skillId, {
        skillName: newName, skillType: newType, description: newDescription, targetAgentCount,
      })
      setName(res.name)
      setType(res.type)
      setDescription(res.description)
      setTarget(res.target_agent_count)
      onSkillUpdated?.(skillId, {
        name: res.name, type: res.type, description: res.description,
        target_agent_count: res.target_agent_count,
      })
      setEditOpen(false)
    } catch (e) {
      setEditError(e.message)
    } finally {
      setEditBusy(false)
    }
  }

  // Replace (not append) the shown cases with a fresh newest-K query. Fetching the
  // full top-K each time — rather than an OFFSET continuation — keeps the list
  // correct if a ticket arrives between loads: no duplicates, no skipped cases.
  async function handleShowMore() {
    setLoadingMore(true)
    try {
      const d = await fetchSkillEvidence(skillId, Math.min(evidenceTotal, EVIDENCE_CAP))
      setEvidence(d.evidence ?? [])
      setEvidenceTotal(d.total ?? 0)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingMore(false)
    }
  }

  // Uniform chip width sized to the longest ticket number currently shown. Ticket
  // numbers are uniform-width within a tenant, so this fits that tenant's format
  // and adapts automatically to a different source. `ch` is the digit advance
  // (badges use tabular-nums, so it's exact); +padding +border complete the box.
  const chipWidth = useMemo(() => {
    const maxLen = evidence.reduce((m, t) => Math.max(m, String(t).length), 0)
    return maxLen ? `calc(${maxLen}ch + 0.45rem + 2px)` : undefined
  }, [evidence])

  const remaining = evidenceTotal - evidence.length
  const canShowMore = remaining > 0 && evidence.length < EVIDENCE_CAP

  function requestSort(key) {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      return null
    })
  }

  const rows = useMemo(() => {
    if (!sort) return agents
    const cmp = SORT_FNS[sort.key]
    const sign = sort.dir === 'desc' ? -1 : 1
    return [...agents].sort((a, b) => sign * cmp(a, b))
  }, [agents, sort])

  // After the Assign modal saves, refetch the member table so the newly-assigned
  // rows appear, and mark the skill dirty so the coverage card refreshes on close.
  async function handleAssigned() {
    dirty.current = true
    try {
      const d = await fetchSkillAgents(skillId)
      setAgents(d.agents ?? [])
    } catch (e) {
      setError(e.message)
    }
  }

  // Whole-skill delete (footer button). Reuses the app-styled ConfirmDialog — a plain
  // confirm, no reason field. On confirm the skill is removed everywhere and GaussIQ
  // stops proposing it; the parent drops its coverage card.
  function confirmDeleteSkill() {
    setConfirm({
      title: 'Delete skill',
      message: `Delete “${name}” for the whole team. This removes it from everyone who `
        + `holds it and stops GaussIQ from proposing it again. This can’t be undone.`,
      confirmLabel: 'Delete Skill',
      variant: 'danger',
      onConfirm: () => { setConfirm(null); runDeleteSkill() },
    })
  }

  async function runDeleteSkill() {
    if (deleting) return
    setDeleting(true)
    try {
      await deleteSkill(skillId)
      onSkillDeleted?.(skillId)
      onClose()
    } catch (e) {
      window.alert(`Could not delete “${name}”: ${e.message}`)
    } finally {
      setDeleting(false)
    }
  }

  // Clickable, keyboard-accessible sort header. The chevron slot keeps a fixed
  // width so toggling sort never shifts the column.
  function sortHeader(key, label, thClass = '') {
    const active = sort?.key === key
    return (
      <th
        className={`${thClass} ${styles.sortable} ${active ? styles.sortActive : ''}`}
        onClick={() => requestSort(key)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); requestSort(key) }
        }}
        role="button"
        tabIndex={0}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <span className={styles.thLabel}>
          {label}
          <span className={styles.sortIcon} aria-hidden="true">
            {active && <Icon name={sort.dir === 'asc' ? 'up' : 'down'} />}
          </span>
        </span>
      </th>
    )
  }

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Skill details">

        <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
          <Icon name="close" />
        </button>

        {/* ── Header — identity, description, mini metrics ── */}
        <div className={styles.header}>
          <div className={styles.identity}>
            <span className={styles.skillIcon}><Icon name="layers" /></span>
            <div>
              <div className={styles.skillNameRow}>
                <span className={styles.skillName}>{name}</span>
                {type && (
                  <SkillPill label={type} variant={String(type).toLowerCase()} />
                )}
                <button
                  className={styles.editBtn}
                  onClick={() => { setEditError(''); setEditOpen(true) }}
                  title="Edit skill"
                  aria-label="Edit skill">
                  <Icon name="pencil" />
                </button>
              </div>
            </div>
          </div>

          <div className={styles.metricsRow}>
            {loading.metrics
              ? Array(3).fill(0).map((_, i) => <div key={i} className={styles.metricSkeleton} />)
              : metrics.map(m => (
                  <MetricCard
                    key={m.name}
                    name={m.name}
                    value={m.value}
                    description={m.description}
                    cardtype={m.cardtype}
                    icon={m.icon}
                    evidenceHeader={m.evidence_header}
                    evidence={m.evidence}
                    compact
                  />
                ))
            }
          </div>
        </div>

        {/* ── Evidence cases — independently scrollable ── */}
        <div className={styles.evidencePanel}>
          <div className={styles.evidenceHead}>
            <span className={styles.evidenceIcon}><Icon name="doc" /></span>
            Evidence Cases
            {evidenceTotal > evidence.length && (
              <span className={styles.evidenceCount}>
                · showing {evidence.length} of {evidenceTotal}
              </span>
            )}
          </div>
          <div className={styles.evidenceGrid} style={{ '--chip-w': chipWidth }}>
            {loading.evidence
              ? <span className={styles.muted}>Loading evidence…</span>
              : evidence.length === 0
                ? <span className={styles.muted}>No evidence cases.</span>
                : <>
                    {evidence.map((t, i) => (
                      <CaseBadge key={`${t}-${i}`} caseId={t} primarySkill={name} />
                    ))}
                    {canShowMore && (
                      <button
                        type="button"
                        className={styles.moreChip}
                        onClick={handleShowMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? 'Loading…' : `+${remaining} more`}
                      </button>
                    )}
                  </>
            }
          </div>
        </div>

        {/* ── Team members holding this skill ─────────── */}
        <div className={styles.tablePanel}>
          {loading.agents ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading team members…</p>
            </div>
          ) : error && agents.length === 0 ? (
            <div className={styles.errorState}>Failed to load: {error}</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  {sortHeader('name', 'Team Member')}
                  {sortHeader('level', 'Level')}
                  {sortHeader('count', '30-Day Count', styles.countCol)}
                  {sortHeader('last_used', 'Last Used')}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={4} className={styles.empty}>No team members have this skill.</td></tr>
                ) : rows.map(row => (
                  <tr key={row.agent_id}>
                    <td className={styles.memberName}>{row.name}</td>
                    <td><span className={styles.level}>{row.level}</span></td>
                    <td className={styles.numeric}>{row.count}</td>
                    <td className={styles.muted}>{formatDate(row.last_used)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────── */}
        <div className={styles.footer}>
          <button
            className={`${styles.footerBtn} ${styles.footerPrimary}`}
            type="button"
            onClick={() => setAssignOpen(true)}>
            Assign
          </button>
          <button
            className={`${styles.footerBtn} ${styles.footerDanger}`}
            type="button"
            onClick={confirmDeleteSkill}
            disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>

      </div>

      {confirm && (
        <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />
      )}

      {assignOpen && (
        <AssignSkillModal
          skillId={skillId}
          skillName={name}
          existingAgentIds={agents.map(a => a.agent_id)}
          onClose={() => setAssignOpen(false)}
          onAssigned={handleAssigned}
        />
      )}

      {editOpen && (
        <EditSkillModal
          currentName={name}
          currentType={type}
          currentDescription={description}
          currentTarget={target}
          types={types}
          busy={editBusy}
          error={editError}
          onSave={handleEditSave}
          onCancel={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}
